// The errorCodes a screen reacts to (shared contract of the CRM restructuring,
// run A). Every other refusal is worded by its HTTP status: the api sends a
// code only when the screen must act on it (drop a row, show a gone screen,
// mark a field).
export const KEPT_ERROR_CODES = [
  'COMPANY_NOT_FOUND',
  'GROUP_NOT_FOUND',
  'CONTACT_NOT_FOUND',
  'INVALID_ID',
  'MEMBER_NOT_FOUND',
  'CONTACT_EMAIL_TAKEN',
  'ALREADY_SENT',
  'EMAIL_NOT_SCHEDULED',
  'EMAIL_NOT_FOUND',
  'COMMENT_NOT_FOUND',
  'OUTSIDE_CALL_WINDOW',
  'INVALID_CONTACT',
  'PLANNING_FORBIDDEN',
  'INVALID_OWNER',
  'INVALID_PEOPLE',
  'TEAM_VIEW_FORBIDDEN',
  'AI_NOT_CONFIGURED',
  'AI_UNAVAILABLE',
  'COMPANY_NOT_ARCHIVED',
  'UNKNOWN_PERSON',
];

// A conflict or an unavailable service keeps its code whatever it is: only
// the code says what changed meanwhile or what is down.
const KEPT_STATUSES = [409, 503];

/** True for a request axios dropped on purpose (AbortController), not a failure. */
export const isCanceledRequest = (error) =>
  error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';

export const errorCodeOf = (error) => error?.response?.data?.errorCode ?? null;

/** True when the errorCode of a refused call is one a screen may word or act on. */
export const isKeptError = (error) => {
  const code = errorCodeOf(error);
  return (
    !!code &&
    (KEPT_ERROR_CODES.includes(code) ||
      KEPT_STATUSES.includes(error?.response?.status))
  );
};

// The key under errors.byStatus (fr.js, en.js). No response at all is a
// network failure: like a 5xx, worth a later retry.
const statusKeyOf = (error) => {
  const status = error?.response?.status;
  if (status === 401) return 'session';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'notFound';
  if (status === 409) return 'conflict';
  if (status >= 400 && status < 500) return 'invalid';
  return 'unavailable';
};

/**
 * The text of a failed call, or null when it was canceled (nothing to show).
 * A kept errorCode with its own text under `group` of `text` (a useText text
 * function) gives that text; anything else the message of its HTTP status,
 * read from errors.byStatus through `language` (the Language manager that
 * useText returns). The api's and axios's messages are never shown.
 */
export const apiErrorText = (error, { language, text, group } = {}) => {
  if (isCanceledRequest(error)) return null;
  if (text && isKeptError(error)) {
    const code = errorCodeOf(error);
    const ownText = text(group ? `${group}.${code}` : code);
    if (ownText) return ownText;
  }
  return language?.getValue(`errors.byStatus.${statusKeyOf(error)}`, '') ?? '';
};

// The AI that drafts a challenge is not set up on this api: no use offering
// it again. AI_UNAVAILABLE, a 503 too, is a passing failure: worth a retry.
export const isAiNotConfigured = (error) =>
  errorCodeOf(error) === 'AI_NOT_CONFIGURED' ||
  (error?.response?.status === 503 && errorCodeOf(error) !== 'AI_UNAVAILABLE');
